import React, { useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PRIORITY } from "@/lib/ui";
import { toast } from "sonner";
import { AssigneePicker } from "@/components/Assignees";

export default function CreateTaskDialog({ boardId, stageId, stages, users, clients = [], onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [stage, setStage] = useState(stageId || stages[0]?.id);
  const [assignees, setAssignees] = useState([]);
  const [due, setDue] = useState("");
  const [labels, setLabels] = useState("");
  const [clientId, setClientId] = useState("none");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      await api.post("/tasks", {
        board_id: boardId, stage_id: stage, title, description: desc, priority,
        assignee_ids: assignees,
        client_id: clientId === "none" ? null : clientId,
        labels: labels.split(",").map((s) => s.trim()).filter(Boolean),
        due_date: due ? new Date(due).toISOString() : null,
      });
      toast.success("Task created");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create task");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-task-dialog">
        <DialogHeader><DialogTitle className="font-display">Create task</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to be done?" autoFocus data-testid="task-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Add detail…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="task-stage-create-select"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="task-priority-create-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assignees</Label>
              <AssigneePicker value={assignees} onChange={setAssignees} staff={users} testid="task-assignee-create-select" />
            </div>
            <div className="space-y-2">
              <Label>Due date</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-due-create-input" />
            </div>
            <div className="space-y-2">
              <Label>Client (optional)</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="task-client-create-select"><SelectValue placeholder="No client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Labels (comma separated)</Label>
            <Input value={labels} onChange={(e) => setLabels(e.target.value)} placeholder="Pilot, Bug, Infra" data-testid="task-labels-create-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="task-create-submit">
            {loading ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
